import { Component, AfterViewInit, ViewChild, ElementRef, OnDestroy, OnChanges, SimpleChanges, input, computed, signal } from '@angular/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(...registerables);

@Component({
  selector: 'app-donut-chart',
  templateUrl: './donut-chart.component.html',
  styleUrl: './donut-chart.component.scss'
})
export class DonutChartComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('donutCanvas') donutCanvas!: ElementRef<HTMLCanvasElement>;

  readonly segments = input.required<{ label: string, value: number, color: string }[]>();
  totalValue = computed(() => this.segments().reduce((acc, item) => acc + item.value, 0));

  private chart?: Chart;

  loading = signal<boolean>(true);

  ngAfterViewInit(): void {
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.chart) return;

    const segmentsChanged = changes['segments']?.previousValue !== changes['segments']?.currentValue;
    const colorsChanged = changes['colors']?.previousValue !== changes['colors']?.currentValue;

    if (segmentsChanged || colorsChanged) {
      this.updateChart();
    }
  }

  private createChart(): void {    
    const chartData = {
      labels: this.segments().map(item => item.label),
      datasets: [{
        data: this.segments().map(item => item.value),
        backgroundColor: this.segments().map(item => item.color),
        borderRadius: 90,
        borderWidth: 6,
        cutout: '70%',
      }]
    };

    const config: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            padding: 16,
            bodySpacing: 10,
            boxPadding: 10,
            caretPadding: 10,
            callbacks: {
              label: function (context) {
                const value = context.parsed;
                return `${value} Query`;
              }
            }
          },
          datalabels: {
            display: true,
            color: '#fff',
            formatter: (value: number, context: any) => {
              const total = context.dataset.data.reduce((acc: number, val: number) => acc + val, 0);
              const percentage = ((value / total) * 100).toFixed();
              return `${percentage}%`;
            },
            font: {
              size: 8,
              weight: 'bold'
            }
          }
        }
      },
      plugins: [ChartDataLabels]
    };

    this.chart = new Chart(this.donutCanvas.nativeElement, config);
  }

  private updateChart(): void {
    if (this.chart) {
      this.loading.set(false);
      this.chart.data.labels = this.segments().map(item => item.label);
      this.chart.data.datasets[0].data = this.segments().map(item => item.value);
      this.chart.data.datasets[0].backgroundColor = this.segments().map(item => item.color);
      this.chart.update();
    }
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }
}
